import {
  Button,
  ConfigProvider,
  Flex,
  Space,
  Table,
  type TableRef,
  TableRowMenuCell,
  Tag,
  Text,
  useTableRowSelection,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { budgetType } from '@/features/master-table/models';
import {
  getExpenditureBudgetPurposeTableName,
  sortExpenditureItemsData,
} from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import { PREVIEW_ROW_ID } from '@/features/project/_utils/constants';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import {
  TABLE_MONETARY_AMOUNT_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  formatValueByUnit,
} from '@/utils';
import classNames from 'classnames';
import { type FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type {
  ExpenditureItemModel,
  ExpenditureItemsModel,
} from '../../../../_models/expenditure-item';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { usePreviewRowState } from '../shared/PreviewRowStateProvider';
import styles from './ExpenditureItemsTable.module.scss';

type Props = {
  data: z.infer<typeof ExpenditureItemsModel>;
  subProjectId: string;
  expenditureCartId: string;
  isUseAccountType: boolean;
  onClickDelete: (value: z.infer<typeof ExpenditureItemModel>) => void;
};

const tableStyles = classNames(styles.ant_table_content);
const tableHeaderStyle = { fontSize: 12 };

const MINIMUM_ROW_NUM_TO_SCROLL = 5;
const PREVIEW_ROW: z.infer<typeof ExpenditureItemModel> = {
  id: PREVIEW_ROW_ID,
  createdAt: '',
  updatedAt: '',
  priorId: null,
  expenditureCartId: '',
  expenditureBudgetPurposeId: null,
  expenditureBudgetPurposeDescription: null,
  budgetTypeId: '',
  requestedAmount: null,
  finalizedAmount: null,
  remark: null,
  expenditureBudgetPurpose: null,
  budgetType: {
    id: '',
    type: budgetType.options[0],
    name: '',
    fiscalYear: 0,
    sortOrder: 1,
    createdAt: '',
    updatedAt: '',
  },
};

export const ExpenditureItemsTable: FC<Props> = ({
  data,
  subProjectId,
  expenditureCartId,
  isUseAccountType,
  onClickDelete,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const tableRef = useRef<TableRef>(null);
  const { id } = useId();
  const { setDrawerState } = useProjectDetailDrawerState();
  const project = useProject(id);
  const editPermission = useProjectPermission(true);
  const { selectedRowKeys, setSelectedRowKeys } = useTableRowSelection();
  const { previewRowState, setPreviewRowState } = usePreviewRowState();

  const showPreviewRow = useMemo(() => {
    return (
      previewRowState?.type === 'expenditure-item' &&
      previewRowState.expenditureCartId === expenditureCartId
    );
  }, [previewRowState, expenditureCartId]);

  const tableData = showPreviewRow ? [...data, PREVIEW_ROW] : data;

  const tableTitle = project.data?.majorExpenditureBudgetPurposeId
    ? `${t('budget-type')}/${t('expenditure-purpose-level-3-1')}`
    : `${t('budget-type')}/${t('expenditure-budget-purpose')}`;

  useEffect(() => {
    if (showPreviewRow && data.length > MINIMUM_ROW_NUM_TO_SCROLL) {
      tableRef.current?.scrollTo({
        index: data.length - MINIMUM_ROW_NUM_TO_SCROLL,
      });
    }
  }, [showPreviewRow, data]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: '#EAEAEA',
            headerSplitColor: '#A0A0A0',
            rowExpandedBg: '#FAFAFA',
          },
        },
      }}
    >
      <Table
        enableSticky={false}
        rowKey="id"
        className={tableStyles}
        dataSource={sortExpenditureItemsData(tableData)}
        locale={{ emptyText: ' ' }}
        rowSelection={{
          renderCell: () => null,
          hideSelectAll: true,
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={[
          {
            title: tableTitle,
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
            render: (_, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return (
                  <Text
                    weight="regular"
                    color="secondary"
                    text={t('preview-row-description', {
                      item: tableTitle,
                    })}
                  />
                );
              }
              return (
                <Space.Compact direction="vertical" size="small">
                  <Space size={0}>
                    <Tag style={{ margin: 0 }}>{record.budgetType.name}</Tag>
                  </Space>
                  <Text
                    color="secondary"
                    weight="regular"
                    text={getExpenditureBudgetPurposeTableName(
                      record.expenditureBudgetPurpose,
                      record.expenditureBudgetPurposeDescription,
                    )}
                    maxWidth={400}
                  />
                </Space.Compact>
              );
            },
          },
          {
            title: t('expenditure-item-remark'),
            key: 'remark',
            dataIndex: 'remark',
            width: 360,
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
            render: (_, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return '';
              }
              return (
                <Text
                  variant="footnote"
                  color="secondary"
                  text={record.remark}
                  maxRows={3}
                />
              );
            },
          },
          {
            title: t('finalized-amount'),
            key: 'finalizedAmount',
            dataIndex: 'finalizedAmount',
            align: 'right',
            width: TABLE_MONETARY_AMOUNT_WIDTH,
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return '';
              }
              return (
                <Text
                  weight="regular"
                  color="secondary"
                  text={formatValueByUnit(value, { operation: 'round', unit })}
                />
              );
            },
          },
          {
            title: t('next-request-amount'),
            key: 'requestedAmount',
            dataIndex: 'requestedAmount',
            align: 'right',
            width: TABLE_MONETARY_AMOUNT_WIDTH,
            onHeaderCell: () => ({
              style: tableHeaderStyle,
            }),
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return '';
              }
              return (
                <Text
                  weight="regular"
                  color="secondary"
                  text={formatValueByUnit(value, { operation: 'round', unit })}
                />
              );
            },
          },
          ...(project.data && !project.data.isLocked && editPermission.data
            ? [
                {
                  title: isUseAccountType && (
                    <PublishedProjectMutableGuard>
                      <Button
                        onClick={() => {
                          setDrawerState({
                            open: true,
                            type: 'expenditure-item',
                            isEditing: false,
                            subProjectId,
                            expenditureCartId,
                          });
                          setPreviewRowState({
                            type: 'expenditure-item',
                            expenditureCartId,
                          });
                          setSelectedRowKeys([PREVIEW_ROW_ID]);
                        }}
                      >
                        {t('create')}
                      </Button>
                    </PublishedProjectMutableGuard>
                  ),
                  key: 'actions',
                  width: TABLE_ROW_MENU_WIDTH,
                  render: (_, record) => {
                    if (record.id === PREVIEW_ROW_ID) {
                      return '';
                    }
                    return (
                      <PublishedProjectMutableGuard>
                        <Flex justify="center">
                          <TableRowMenuCell
                            onEdit={() => {
                              setDrawerState({
                                open: true,
                                type: 'expenditure-item',
                                isEditing: true,
                                subProjectId,
                                expenditureCartId: record.expenditureCartId,
                                expenditureItemId: record.id,
                              });
                              setSelectedRowKeys([record.id]);
                            }}
                            items={[
                              {
                                key: 'delete',
                                label: t('delete'),
                                danger: true,
                                onClick: () => {
                                  onClickDelete(record);
                                },
                              },
                            ]}
                          />
                        </Flex>
                      </PublishedProjectMutableGuard>
                    );
                  },
                },
              ]
            : []),
        ]}
        pagination={false}
      />
    </ConfigProvider>
  );
};
